import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { AsyncSelect, AsyncSelectProps, GroupProps, GroupBase } from '@any-ui-react/select';

import { SearchIcon } from '@anyx/shared-icons';

export type GroupFilterOption<T = object> = {
  value: string;
  label: ReactNode;
} & T;

export type GroupFilterGroup<T = object> = GroupBase<GroupFilterOption<T>> & {
  label: ReactNode;
  options: GroupFilterOption<T>[];
  remainingCount?: number;
  icon?: ReactNode;
};

const SearchBarControlPrefixIcon = () => {
  return <SearchIcon className="text-gray-7 h-4 w-4" />;
};

const Group = <T = object,>({
  data: { icon, remainingCount, label },
  children,
}: GroupProps<GroupFilterOption<T>, false, GroupFilterGroup<T>>) => {
  const { t } = useTranslation('anychat');
  return (
    <div className="-mx-2 flex flex-col px-4 first:border-b first:border-[#DEE5EC] first:py-2">
      <span className="text-gray-6 text-2xs mb-2 flex items-center gap-x-1">
        {icon}
        {label}
      </span>
      {children}
      {!!remainingCount && remainingCount > 0 && (
        <span className="text-gray-6 text-2xs flex items-center">
          {t('anychat.shared.crmSearchBar.moreResults', { remainingCount })}
        </span>
      )}
    </div>
  );
};

export type GroupFilterSelectorProps<T = object> = AsyncSelectProps<
  GroupFilterOption<T>,
  false,
  GroupFilterGroup<T>
>;

export const GroupFilterSelector = <T = object,>(props: GroupFilterSelectorProps<T>) => {
  return (
    <AsyncSelect
      isSearchable
      isClearable
      components={{
        Group,
      }}
      classNames={{
        menuList: () => 'gap-y-4 flex flex-col m-0 ',
        option: (state) =>
          state.isDisabled
            ? 'my-2 text-center text-2xs! text-gray-6! hover:bg-transparent! cursor-default! bg-transparent!'
            : '',
      }}
      controlPrefixIcon={SearchBarControlPrefixIcon}
      {...props}
    />
  );
};
